import { App } from './App';
import { ApplicationProvider } from 'hooks/useApplication';
import { AppMap } from './routes';
import { AuthProvider } from 'hooks/useAuth';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ClientBlockedStatusProvider } from 'hooks/useClientBlockedStatus';
import { ClientConstraintsProvider } from 'hooks/useClientConstraints';
import { ClientOfferProvider } from 'hooks/useClientOffer';
import {
  datadogReportLogs,
  getTinkReportIdsFromSearch,
  hasTinkReportIds,
  saveTinkReportsToCookies,
} from 'utils/tinkReports';
import { fakeDataConfig } from 'utils/fakeDataConfig';
import { isNotProduction } from 'utils/isNotProduction';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TestingTools } from 'InternalLibrary';
import { useAnalyticsCookies } from 'hooks/useAnalyticsCookies';
import { useGTMModal } from 'hooks/utilities/useGTMModal';
import { useLocation } from 'react-router-dom';
import DataLayer from 'utils/dataLayer/DataLayer';
import EventCollectorContextProvider from 'hooks/eventCollector/EventCollectorContextProvider';
import GlobalNotification from 'components/GlobalNotification/GlobalNotification';
import GuardRoute from './GuardRoute';
import PageEvent from 'hooks/eventCollector/events/PageEvent';
import React, { lazy, Suspense, useEffect } from 'react';
import useClient, { ClientProvider } from 'hooks/useClient';
import useEventCollector from 'hooks/eventCollector/useEventCollector';
import useFirebase, { FirebaseProvider } from 'hooks/useFirebase';
import useSmartlook from 'hooks/useSmartlook';

const LazyBuildInfo = lazy(() => import('utils/BuildInfo'));

const queryClient = new QueryClient();

export default function Router() {
  const isDevEnv = isNotProduction();
  return (
    <QueryClientProvider client={queryClient}>
      <App.Provider>
        <EventCollectorContextProvider>
          <AuthProvider>
            <ClientProvider>
              <ApplicationProvider>
                <ClientConstraintsProvider>
                  <ClientOfferProvider>
                    <ClientBlockedStatusProvider>
                      <FirebaseProvider>
                        <BrowserRouter>
                          <AppRoutes />
                          <DataLayer />
                          <GlobalNotification />
                          {isDevEnv && (
                            <TestingTools fakeData={fakeDataConfig} />
                          )}
                          <Suspense fallback={null}>
                            {isDevEnv && <LazyBuildInfo />}
                          </Suspense>
                        </BrowserRouter>
                      </FirebaseProvider>
                    </ClientBlockedStatusProvider>
                  </ClientOfferProvider>
                </ClientConstraintsProvider>
              </ApplicationProvider>
            </ClientProvider>
          </AuthProvider>
        </EventCollectorContextProvider>
      </App.Provider>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  useGTMModal(); // GTM modal useEffects to show/hide GTM modal
  const { pathname, search } = useLocation();
  const { page } = useEventCollector();
  const { client } = useClient();
  const { identifyClient } = useSmartlook();
  const { firebaseData } = useFirebase();
  const { initializeCookiesIfNeeded } = useAnalyticsCookies();

  useEffect(() => {
    page(new PageEvent());
  }, [pathname, page]);

  // Catch tink report IDs from redirection in case an error happens when client comes back
  useEffect(() => {
    const ids = getTinkReportIdsFromSearch(search);
    if (hasTinkReportIds(ids)) {
      saveTinkReportsToCookies(ids);
      if (firebaseData.REGISTRATION_DATADOG_LOGS_INFO) {
        datadogReportLogs('router', client);
      }
    }
  }, [client?.id, search, firebaseData.REGISTRATION_DATADOG_LOGS_INFO]);

  useEffect(() => {
    // Scroll to top after rendering
    window.scrollTo(0, 0);

    initializeCookiesIfNeeded();
  }, [pathname]);

  // Smartlook Identify Client
  useEffect(() => {
    if (!isNotProduction()) identifyClient();
  }, [client?.number]);

  return (
    <Routes>
      {Object.keys(AppMap).map((path) => {
        const RouteComponent = AppMap[path].nonDynamicComponent;
        const authenticated: boolean | undefined = AppMap[path].authenticated;
        return (
          <Route
            key={path}
            path={path}
            element={
              <GuardRoute authenticated={authenticated}>
                <RouteComponent />
              </GuardRoute>
            }
          />
        );
      })}
    </Routes>
  );
}
